import {get} from 'lodash';
import {action, observable} from 'mobx';
import {DeviceCategory} from '@techsee/techsee-common/lib/constants/utils.constant';
import {Boundaries} from '@techsee/techsee-ui-common/lib/draggble-panel/contracts';
import {Channel, IWifiAnalyzerResults} from '../../services/AngularServices/WifiAnalyzerService';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {IEventLogsService} from '../../services/EventsLogService';
import {IWifiAnalyzerService} from '../../services/AngularServices/WifiAnalyzerService';
import {ITsEnvironmentDetect} from '@techsee/techsee-common/lib/helpers/ts-environment-detect';
import {WifiScanService} from '../connectivity-guru/wifi-analyzer-scan/WifiScanService';
export const scanTypeMapping = {
    networkSpeed: 'NETWORK LIST',
    channelAnalysis: 'CHANNEL ANALYSIS',
    networkDiscoveryDevices: 'CONNECTED DEVICES',
    connectivityGuruData: '3D HEAT MAP'
};

export interface IWifiAnalyzerController {
    readonly isVisible: boolean;
    readonly results: IWifiAnalyzerResults | null;

    readonly position: {x: number; y: number};
    readonly boundaries: Boundaries;
    readonly offset: Boundaries;
    readonly deviceCategory: DeviceCategory;
    readonly currentTab: number;
    readonly isMinimized: boolean;
    readonly isScanning: boolean;
    readonly channelAnalysis: Channel[];
    readonly insightsText: string;

    show(): void;
    hide(): void;
    setResults(results: IWifiAnalyzerResults): void;
    minimize(): void;
    maximize(): void;
    getFormattedTime(date: string): string;
    setPosition(position: {x: number; y: number}): void;
    setCurrentTab(tab: number): void;
    startScan(): void;
    getDbmColor(dbm: number | undefined): string;
}

export class WifiAnalyzerController implements IWifiAnalyzerController {
    @observable isVisible: boolean = false;

    @observable results: IWifiAnalyzerResults | null = null;

    @observable boundaries: Boundaries = {
        right: window.innerWidth,
        bottom: window.innerHeight,
        left: 0,
        top: 0
    };

    @observable offset: Boundaries = {
        top: 0,
        left: 0,
        bottom: 0,
        right: 0
    };

    @observable position: {x: number; y: number} = {x: 60, y: window.innerHeight - 0.7 * window.innerHeight};
    @observable deviceCategory: DeviceCategory = DeviceCategory.desktop;
    @observable currentTab: number = 0;
    @observable isMinimized: boolean = false;

    @observable isScanning: boolean = false;
    @observable channelAnalysis: Channel[] = [];

    private wifiAnalyzerService: any;
    private getDateFormattedByAccLocal: (date: string, format: string) => string;
    private wifiScanService: WifiScanService;

    get insightsText(): string {
        if (!this.results || !this.results.networkDiscoveryDevices) {
            return '';
        }

        const deviceCount = this.results.networkDiscoveryDevices.length;

        if (deviceCount < 3) {
            return 'The scan detected a low device count, indicating a simple network, which can be leveraged by offering a free consultation to build trust and introduce a wider range of products.';
        } else if (deviceCount >= 3 && deviceCount <= 5) {
            return 'The scan detected a medium device count, suggesting a standard smart home, which can be leveraged by offering a "Smart Home Optimization" service to enhance user setup.';
        } else if (deviceCount >= 6 && deviceCount <= 10) {
            return 'The scan detected a high device count, which points to a complex ecosystem, and can be leveraged by marketing a "Premium Network Management" package for recurring subscription.';
        } else if (deviceCount > 10) {
            return "The scan detected an unexpectedly high number of devices, signaling a potential breach, which can be leveraged by offering a complimentary security audit to demonstrate the company's value.";
        }

        return '';
    }

    constructor(
        environmentService: ITsEnvironmentDetect,
        private eventsLogService: IEventLogsService,
        wifiAnalyzerService: IWifiAnalyzerService,
        getDateFormattedByAccLocal: (date: string, format: string) => string,
        displayTabletAsDesktop: boolean
    ) {
        this.wifiAnalyzerService = wifiAnalyzerService;
        this.getDateFormattedByAccLocal = getDateFormattedByAccLocal;
        this.deviceCategory = environmentService.deviceCategory(displayTabletAsDesktop) || DeviceCategory.desktop;
        this.wifiScanService = new WifiScanService();
    }

    @action
    hide = () => {
        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {roomId: this.results?.roomId, status: 'modal closed'}
        });

        this.isVisible = false;
        this.results = null;
    };

    @action
    setIsScanning = (isScanning: boolean) => {
        this.isScanning = isScanning;

        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultInitiated,
            meta: {roomId: this.results?.roomId, isScanning}
        });
    };

    @action
    startScan = async () => {
        this.setIsScanning(true);
        if (this.results) {
            this.wifiAnalyzerService.runScanner(
                this.results.roomId,
                this.results.id,
                (channels: any) => {
                    this.setChannelAnalysis(channels);
                    this.setIsScanning(false);
                },
                () => this.setIsScanning(false)
            );
        }
    };

    @action
    getFormattedTime = (date: string) => this.getDateFormattedByAccLocal(date, 'dd/MM/yyyy hh:mm');

    @action
    show = () => {
        this.isVisible = true;

        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {roomId: this.results?.roomId, status: 'modal opened'}
        });

        const newLeftBar = (document.getElementsByClassName('app-left-bar') as any)[0];
        const newTopBar = (document.getElementsByClassName('app-top-bar ') as any)[0];

        this.setOffset({
            left: get(newLeftBar, 'offsetWidth', 0),
            top: get(newTopBar, 'offsetHeight', 0),
            right: 0,
            bottom: 0
        });
    };

    @action
    setResults = (results: IWifiAnalyzerResults) => {
        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {roomId: results.roomId, results, status: 'results set'}
        });

        this.channelAnalysis = results.channelAnalysis;
        this.results = results;
    };

    @action
    setChannelAnalysis = (channelAnalysis: Channel[]) => {
        this.eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultModal,
            meta: {roomId: this.results?.roomId, channelAnalysis, status: 'channel analysis set'}
        });

        this.channelAnalysis = channelAnalysis;
    };

    @action
    setCurrentTab = (tab: number) => {
        this.currentTab = tab;
    };

    @action
    minimize = () => {
        this.isMinimized = true;

        const position = {
            y: this.boundaries.bottom - 60,
            x: 60
        };

        this.setPosition(position);
    };

    @action
    maximize = () => {
        this.isMinimized = false;

        const position = {
            y: this.boundaries.bottom - this.boundaries.bottom / 2 - 100,
            x: 60
        };

        this.setPosition(position);
    };

    @action
    setPosition = (position: {x: number; y: number}) => {
        this.position = position;
    };

    @action
    setOffset = (offset: Boundaries) => {
        this.offset = offset;
    };

    getDbmColor = (dbm: number | undefined): string => {
        return this.wifiScanService.getDbmColor(dbm, this.results?.rssiConfig);
    };
}
